import { useEffect, useRef, useState } from "react";
import { Anchor, Badge, Button, Card, Chip, Group, Modal, Stack, Switch, Table, Text, TextInput, Title } from "@mantine/core";
import { IconBrandTelegram, IconCheck, IconClock, IconLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { notifications as notify } from "@mantine/notifications";
import QRCode from "qrcode";

import { api } from "../../api/client";
import type { NotificationPreference, NotificationType } from "../../api/types";
import { NOTIFICATION_TYPES } from "../../api/types";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";

const WORKING_HOURS_KEY = "notifications.working_hours";
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

interface WorkingHours {
  enabled: boolean;
  start: string;
  end: string;
  days: number[]; // ISO 1=Mon..7=Sun
}
const DEFAULT_WH: WorkingHours = { enabled: false, start: "09:00", end: "18:00", days: [1, 2, 3, 4, 5] };

export function NotificationsSettingsTab() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const [prefs, setPrefs] = useState<Record<NotificationType, NotificationPreference>>(
    {} as Record<NotificationType, NotificationPreference>,
  );
  const [loading, setLoading] = useState(true);
  const debounceRefs = useRef<Record<string, number>>({});
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [wh, setWh] = useState<WorkingHours>(DEFAULT_WH);
  const [whSaving, setWhSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      const list = await api.getNotificationPreferences();
      const map = {} as Record<NotificationType, NotificationPreference>;
      for (const p of list) map[p.type] = p;
      setPrefs(map);
      setLoading(false);
    })();
    void (async () => {
      try {
        const res = await api.getSetting(WORKING_HOURS_KEY);
        if (res.value && typeof res.value === "object") {
          setWh({ ...DEFAULT_WH, ...(res.value as Partial<WorkingHours>) });
        }
      } catch {
        // keep defaults
      }
    })();
  }, []);

  async function saveWorkingHours(next: WorkingHours) {
    setWh(next);
    setWhSaving(true);
    try {
      await api.putSetting(WORKING_HOURS_KEY, next);
      notify.show({ color: "green", message: t("settings.notifications.wh.saved"), autoClose: 1200 });
    } catch (e) {
      notify.show({ color: "red", message: String(e) });
    } finally {
      setWhSaving(false);
    }
  }

  function toggleDay(iso: number) {
    const days = wh.days.includes(iso)
      ? wh.days.filter((d) => d !== iso)
      : [...wh.days, iso].sort((a, b) => a - b);
    void saveWorkingHours({ ...wh, days });
  }

  function update(type: NotificationType, patch: Partial<NotificationPreference>) {
    const next: NotificationPreference = { ...prefs[type], ...patch, type };
    setPrefs((s) => ({ ...s, [type]: next }));
    const key = type;
    if (debounceRefs.current[key]) window.clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = window.setTimeout(() => {
      void api.setNotificationPreference(next);
    }, 500);
  }

  async function startLink() {
    try {
      const res = await api.telegramLinkStart();
      setLinkUrl(res.url);
      const qr = await QRCode.toDataURL(res.url, { width: 240, margin: 1 });
      setQrDataUrl(qr);
      setLinkOpen(true);
    } catch (err) {
      alert(t("settings.notifications.linkFailed") + ": " + String(err));
    }
  }

  async function disconnect() {
    if (!confirm(t("settings.notifications.disconnectConfirm"))) return;
    await api.telegramDisconnect();
    window.location.reload();
  }

  const telegramConnected = user.telegram_connected;

  return (
    <Stack>
      <Card withBorder>
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <IconBrandTelegram size={20} />
            <Title order={5}>{t("settings.notifications.telegramCardTitle")}</Title>
          </Group>
          {telegramConnected ? (
            <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
              {t("settings.notifications.connected")}
            </Badge>
          ) : (
            <Badge color="gray" variant="light">
              {t("settings.notifications.notConnected")}
            </Badge>
          )}
        </Group>
        {telegramConnected ? (
          <Group>
            <Text size="sm" c="dimmed">{t("settings.notifications.connectedHelp")}</Text>
            <Button color="red" variant="light" size="xs" onClick={() => void disconnect()}>
              {t("settings.notifications.disconnect")}
            </Button>
          </Group>
        ) : (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">{t("settings.notifications.connectHelp")}</Text>
            <Group>
              <Button leftSection={<IconLink size={14} />} onClick={() => void startLink()}>
                {t("settings.notifications.connect")}
              </Button>
            </Group>
          </Stack>
        )}
      </Card>

      <Card withBorder>
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <IconClock size={20} />
            <Title order={5}>{t("settings.notifications.wh.title")}</Title>
          </Group>
          <Switch
            checked={wh.enabled}
            onChange={(e) => void saveWorkingHours({ ...wh, enabled: e.currentTarget.checked })}
            label={wh.enabled ? t("settings.notifications.wh.on") : t("settings.notifications.wh.off")}
          />
        </Group>
        <Text size="sm" c="dimmed" mb="sm">{t("settings.notifications.wh.help")}</Text>
        <Group align="flex-end" gap="sm" mb="sm">
          <TextInput
            type="time"
            label={t("settings.notifications.wh.start")}
            value={wh.start}
            disabled={!wh.enabled}
            onChange={(e) => setWh({ ...wh, start: e.currentTarget.value })}
            onBlur={() => void saveWorkingHours(wh)}
            w={140}
          />
          <TextInput
            type="time"
            label={t("settings.notifications.wh.end")}
            value={wh.end}
            disabled={!wh.enabled}
            onChange={(e) => setWh({ ...wh, end: e.currentTarget.value })}
            onBlur={() => void saveWorkingHours(wh)}
            w={140}
          />
        </Group>
        <Text size="sm" fw={500} mb={4}>{t("settings.notifications.wh.days")}</Text>
        <Group gap="xs">
          {DAY_KEYS.map((dk, idx) => {
            const iso = idx + 1;
            return (
              <Chip
                key={dk}
                checked={wh.days.includes(iso)}
                disabled={!wh.enabled || whSaving}
                onChange={() => toggleDay(iso)}
                size="sm"
              >
                {t(`settings.notifications.wh.day.${dk}`)}
              </Chip>
            );
          })}
        </Group>
      </Card>

      <Card withBorder>
        <Title order={5} mb="xs">{t("settings.notifications.prefsCardTitle")}</Title>
        {loading ? <Text c="dimmed">{t("common.loading")}</Text> : (
          <ResponsiveTable
            mobile={{
              rows: [...NOTIFICATION_TYPES],
              rowKey: (type) => type,
              title: (type) => t(`settings.notifications.typeLabel.${type}`),
              subtitle: (type) => type,
              alwaysOpen: true,
              fields: [
                {
                  label: t("settings.notifications.colInApp"),
                  render: (type) => (
                    <Switch
                      checked={(prefs[type] ?? { in_app: true }).in_app}
                      onChange={(e) => update(type, { in_app: e.currentTarget.checked })}
                    />
                  ),
                },
                {
                  label: t("settings.notifications.colTelegram"),
                  render: (type) => (
                    <Switch
                      checked={(prefs[type] ?? { telegram: false }).telegram}
                      disabled={!telegramConnected}
                      onChange={(e) => update(type, { telegram: e.currentTarget.checked })}
                    />
                  ),
                },
              ],
            }}
          >
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("settings.notifications.colType")}</Table.Th>
                <Table.Th>{t("settings.notifications.colInApp")}</Table.Th>
                <Table.Th>{t("settings.notifications.colTelegram")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {NOTIFICATION_TYPES.map((type) => {
                const p = prefs[type] ?? { type, in_app: true, telegram: false };
                return (
                  <Table.Tr key={type}>
                    <Table.Td>
                      <Text size="sm">{t(`settings.notifications.typeLabel.${type}`)}</Text>
                      <Text size="xs" c="dimmed">{type}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={p.in_app}
                        onChange={(e) => update(type, { in_app: e.currentTarget.checked })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={p.telegram}
                        disabled={!telegramConnected}
                        onChange={(e) => update(type, { telegram: e.currentTarget.checked })}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </ResponsiveTable>
        )}
      </Card>

      <Modal opened={linkOpen} onClose={() => setLinkOpen(false)} title={t("settings.notifications.linkModalTitle")} centered>
        <Stack align="center">
          {qrDataUrl && <img src={qrDataUrl} alt="QR" style={{ width: 240, height: 240 }} />}
          <Text size="sm" c="dimmed" ta="center">{t("settings.notifications.linkModalHelp")}</Text>
          {linkUrl && <Anchor href={linkUrl} target="_blank">{linkUrl}</Anchor>}
        </Stack>
      </Modal>
    </Stack>
  );
}
